import { Send, MailOpen, MousePointerClick, AlertTriangle, UserMinus, DollarSign } from "lucide-react";
import Drawer from "../../../components/ui/Drawer";
import Badge from "../../../components/ui/Badge";
import Sparkline from "../../../components/ui/Sparkline";
import { CAMPAIGN_STATUS } from "./campaign.data";

function FunnelRow({ label, value, pct, color }) {
  return (
    <div>
      <div className="flex items-center justify-between mb-1.5">
        <span className="text-xs font-medium text-text_secondary">{label}</span>
        <span className="text-xs font-bold text-text_primary mono">{value.toLocaleString()}</span>
      </div>
      <div className="h-2 rounded-full bg-surface_alt overflow-hidden">
        <div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, background: color }} />
      </div>
    </div>
  );
}

function MiniStat({ icon: Icon, label, value, color }) {
  return (
    <div className="rounded-xl border border-border bg-surface_alt/40 p-3">
      <div className="flex items-center gap-1.5 text-text_muted mb-1.5">
        <Icon className="w-3.5 h-3.5" style={{ color }} />
        <span className="text-[11px] font-medium">{label}</span>
      </div>
      <div className="text-lg font-bold text-text_primary leading-none">{value}</div>
    </div>
  );
}

export default function CampaignDetailDrawer({ campaign, open, onClose }) {
  if (!campaign) return null;
  const c = campaign;
  const st = CAMPAIGN_STATUS[c.status] ?? CAMPAIGN_STATUS.draft;
  const opened = Math.round((c.delivered * c.openRate) / 100);
  const clicked = Math.round((c.delivered * c.clickRate) / 100);
  const maxF = c.sent || 1;

  return (
    <Drawer
      open={open}
      onClose={onClose}
      title={c.name}
      subtitle={c.subject}
      icon={Send}
      width={560}
      footer={
        <div className="flex items-center justify-end gap-2">
          <button className="px-4 py-2 rounded-xl text-sm font-medium border border-border text-text_secondary hover:bg-surface_alt transition-colors">
            Duplicate
          </button>
          <button
            className="px-4 py-2 rounded-xl text-sm font-semibold text-white"
            style={{ background: "linear-gradient(135deg, var(--color-primary) 0%, var(--color-secondary) 100%)" }}
          >
            View full report
          </button>
        </div>
      }
    >
      <div className="space-y-6">
        {/* Meta */}
        <div className="flex flex-wrap items-center gap-2">
          <Badge tone={st.tone} dot>{st.label}</Badge>
          <Badge tone="primary">{c.type}</Badge>
          <Badge tone="neutral">{c.audience}</Badge>
        </div>

        {/* Headline metrics */}
        <div className="grid grid-cols-3 gap-3">
          <MiniStat icon={MailOpen} label="Open rate" value={c.sent ? `${c.openRate}%` : "—"} color="var(--color-success)" />
          <MiniStat icon={MousePointerClick} label="Click rate" value={c.sent ? `${c.clickRate}%` : "—"} color="var(--color-info)" />
          <MiniStat icon={DollarSign} label="Revenue" value={c.revenue ? `$${c.revenue.toLocaleString()}` : "$0"} color="var(--color-primary)" />
        </div>

        {/* Engagement funnel */}
        <div className="rounded-xl border border-border bg-surface p-4 space-y-3.5">
          <h4 className="text-sm font-semibold text-text_primary">Engagement funnel</h4>
          {c.sent === 0 ? (
            <p className="text-xs text-text_muted py-2">No send data yet — this campaign hasn't gone out.</p>
          ) : (
            <>
              <FunnelRow label="Sent" value={c.sent} pct={100} color="var(--color-primary)" />
              <FunnelRow label="Delivered" value={c.delivered} pct={(c.delivered / maxF) * 100} color="var(--color-info)" />
              <FunnelRow label="Opened" value={opened} pct={(opened / maxF) * 100} color="var(--color-success)" />
              <FunnelRow label="Clicked" value={clicked} pct={(clicked / maxF) * 100} color="var(--color-secondary)" />
            </>
          )}
        </div>

        {/* Secondary metrics */}
        <div className="grid grid-cols-2 gap-3">
          <MiniStat icon={AlertTriangle} label="Bounce rate" value={`${c.bounceRate}%`} color="var(--color-high)" />
          <MiniStat icon={UserMinus} label="Unsub rate" value={`${c.unsubRate}%`} color="var(--color-danger)" />
        </div>

        {/* Trend */}
        {c.sent > 0 && (
          <div className="rounded-xl border border-border bg-surface p-4">
            <div className="flex items-center justify-between mb-2">
              <h4 className="text-sm font-semibold text-text_primary">Open-rate trend</h4>
              <span className="text-xs text-text_muted">Last 7 intervals</span>
            </div>
            <Sparkline data={c.trend} width={500} height={70} color="var(--color-primary)" strokeWidth={2.4} />
          </div>
        )}

        {/* Details */}
        <div className="rounded-xl border border-border bg-surface divide-y divide-border text-sm">
          {[
            ["Campaign ID", c.id],
            ["Owner", c.owner],
            ["Send time", c.sentAt],
            ["Recipients", c.recipients ? c.recipients.toLocaleString() : "—"],
          ].map(([k, v]) => (
            <div key={k} className="flex items-center justify-between px-4 py-2.5">
              <span className="text-text_muted text-xs">{k}</span>
              <span className="text-text_primary font-medium">{v}</span>
            </div>
          ))}
        </div>
      </div>
    </Drawer>
  );
}
